import { QueryClient, dehydrate } from '@tanstack/react-query';
import axios from 'axios';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import type { GetServerSidePropsContext, InferGetServerSidePropsType, NextPage } from 'next';

import { useCustomRouter } from '$hooks/client';
import { SHOP_QUERY_KEY, Shop } from '$hooks/queries';
import { AuthStatuses, useAuth } from '$src/hooks/auth';
import { useLoadingAtom } from '$src/hooks/state/useLoadingAtom';
import { type PostHogBootstrapData, getPostHogBootstrapData } from '$src/utils/getPostHogBootstrapData';

const CACHE_DURATION_IN_SECONDS = 60;

export const getServerSideProps = async ({ res, req, locale = 'fr', query }: GetServerSidePropsContext) => {
  res.setHeader(
    'Cache-Control',
    `public, s-maxage=${CACHE_DURATION_IN_SECONDS}, stale-while-revalidate=${CACHE_DURATION_IN_SECONDS - 1}`,
  );

  let postHogBootstrapData: PostHogBootstrapData | null = null;
  const bootstrapDistinctId = z.string().safeParse(query?.uid).data;

  try {
    postHogBootstrapData = await getPostHogBootstrapData({
      bootstrapDistinctId,
      requestHeaders: req.headers,
    });
  } catch {
    console.error('Failed to get posthog bootstrap data');
  }

  const shopId = z.string().safeParse(query?.shopId).data;
  if (!shopId) {
    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        postHogBootstrapData,
      },
    };
  }

  const headers = {
    'x-request-id': uuidv4(),
    'getjust-gateway-token': process.env.GETJUST_API_TOKEN,
  };

  const url = process.env.NEXT_PUBLIC_JUST_API_URL + `/v1/shop/${shopId}`;

  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: [SHOP_QUERY_KEY],
    queryFn: async () => (await axios.get<Shop>(url, { headers })).data,
  });

  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      shopId,
      dehydratedState: dehydrate(queryClient),
      postHogBootstrapData,
    },
  };
};

/**
 * This index page is used like a router, redirecting to the correct page to display
 */
const CheckoutForm: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = () => {
  const router = useCustomRouter();
  const { authState } = useAuth() ?? {};
  const { onLoadingChange } = useLoadingAtom();

  useEffect(() => {
    if (authState === AuthStatuses.PENDING) return;
    router.push(`/${authState}`);
  }, [authState, router]);

  useEffect(() => {
    onLoadingChange(authState === AuthStatuses.PENDING);
  }, [onLoadingChange, authState]);

  return <></>;
};

export default CheckoutForm;
